import { Observable, forkJoin, of, throwError, BehaviorSubject } from "rxjs";
import {
  Product,
  ProductType,
  ProductStatus,
  ProductMedia,
  ProductCategory,
  ItineraryStop,
  ProductFeature,
  ProductSession,
  CancellationPolicy,
  ProductAddon,
  SchedulePattern,
  ProductAccessibility,
  ProductHealthSafety,
  MeetingPickUpSetting,
  PickupPointCategory,
  MeetingPoint,
  ResellerType,
  AdditionalInfoTypeEnum,
  PriceUnit,
  GroupDefinitionEnum,
  ProductSeason,
  PricingRecord,
  ProductOption,
  AgeDefinition,
  AgeBand,
  Operator,
  OperatorStatusEnum,
  Affiliate,
  AffiliateStatusEnum,
  AffiliateSiteConfig,
  CompletionStageEnum,
  UpdateOperatorProfileDto,
} from "./tour.models";

export enum FlexibleDurationUnit {
  minutes = "MINUTES",
  hours = "HOURS",
}

export enum DifficultyLevel {
  easy = "Easy",
  moderate = "Moderate",
  difficult = "Difficult",
}

export interface ProductCreationStage {
  basicDetails: boolean;
  productType: boolean;
  categories: boolean;
  media: boolean;
  meetingPoints: boolean;
  tourOrActivityDetails: boolean;
  description: boolean;
  accessibility: boolean;
  pricing: boolean;
  scheduling: boolean;
  bookingSettings: boolean;
}

export abstract class ProductService {
  protected isOperatorSubject = new BehaviorSubject<boolean | null>(null);
  protected operatorSubject = new BehaviorSubject<Operator | null>(null);
  protected affiliateSubject = new BehaviorSubject<Affiliate | null>(null);
  protected initialLoadingSubject = new BehaviorSubject<boolean>(true);
  public isOperator$ = this.isOperatorSubject.asObservable();
  public operator$ = this.operatorSubject.asObservable();
  public affiliate$ = this.affiliateSubject.asObservable();
  public initialLoading$ = this.initialLoadingSubject.asObservable();

  constructor(protected ngZone: import("@angular/core").NgZone) {}

  public clearState(): void {
    this.isOperatorSubject.next(null);
    this.operatorSubject.next(null);
    this.affiliateSubject.next(null);
    this.initialLoadingSubject.next(true);
  }

  public updateStatus(
    operator: Operator | null,
    affiliate: Affiliate | null,
  ): void {
    this.ngZone.run(() => {
      const isActiveOperator = operator?.status === OperatorStatusEnum.ACTIVE;
      this.isOperatorSubject.next(isActiveOperator);
      this.operatorSubject.next(operator);
      this.affiliateSubject.next(affiliate);
      this.initialLoadingSubject.next(false);
    });
  }

  public abstract ensureInitialized(): void;

  abstract getOperator(isReload: boolean): Observable<Operator>;
  abstract checkIfOperator(): Observable<boolean>;
  abstract checkIfActiveOperator(recheck?: boolean): Observable<boolean>;
  abstract becomeTourOperator(profile: any): Observable<Operator>;
  abstract getAffiliate(isReload: boolean): Observable<Affiliate>;
  abstract checkIfAffiliate(): Observable<boolean>;
  abstract checkIfActiveAffiliate(recheck?: boolean): Observable<boolean>;
  abstract becomeAffiliate(profile: any): Observable<Affiliate>;
  abstract getAffiliateStats(): Observable<any>;
  abstract getAffiliateBookings(): Observable<any>;
  abstract getAffiliateSiteConfig(): Observable<any>;
  abstract createAffiliateSiteConfig(formData: FormData): Observable<any>;
  abstract updateAffiliateSiteConfig(formData: FormData): Observable<any>;
  abstract submitAffiliateSiteForApproval(): Observable<any>;
  abstract isTour(product: Product): boolean;
  abstract isActivity(product: Product): boolean;
  abstract getCreationStage(product: Product): ProductCreationStage;

  abstract createProductDraft(
    title: string,
    language: string,
    productType: ProductType,
    productClassification?: string,
  ): Observable<Product>;

  abstract setProductType(
    product: Product,
    type: ProductType,
    classification?: string,
  ): Observable<Product>;

  abstract updateBasicDetails(
    product: Product,
    details: {
      title?: string;
      subtitle?: string;
      briefDescription?: string;
      description?: string;
      whatToBring?: string;
      goodToKnow?: string;
      language?: string;
      isCustomizable?: boolean;
      completionStage?: string; // for override the default
      isPrivate?: boolean;
      productClassification?: string;
    },
  ): Observable<Product>;

  abstract saveAdditionalInfo(
    product: Product,
    settings: {
      resellerType: ResellerType;
      contactPhone: string;
      difficultyLevel: string;
      accessibilities: {
        infoType: AdditionalInfoTypeEnum;
        isApplicable: boolean;
        customText?: string;
      }[];
      healthRestrictions: {
        infoType: AdditionalInfoTypeEnum;
        isApplicable: boolean;
        customText?: string;
      }[];
      generalInfos: {
        infoType: AdditionalInfoTypeEnum;
        isApplicable: boolean;
        customText?: string;
      }[];
    },
  ): Observable<Product>;
  abstract finishMediaStep(product: Product): Observable<Product>;

  abstract finishMeetingPickup(product: Product): Observable<Product>;
  abstract finishDetailConfig(product: Product): Observable<Product>;
  abstract finishFeatureConfig(product: Product): Observable<Product>;
  abstract finishUniqueConfig(product: Product): Observable<Product>;
  abstract finishCancellationPolicy(product: Product): Observable<Product>;
  abstract finishAdditionInfoConfig(product: Product): Observable<Product>;
  abstract finishBasicPricingConfig(product: Product): Observable<Product>;
  abstract finishSchedulePricingConfig(product: Product): Observable<Product>;
  abstract finishBookingSettings(product: Product): Observable<Product>;
  abstract manageCategories(
    product: Product,
    categories: ProductCategory[],
  ): Observable<void>;

  abstract manageMedia(
    product: Product,
    operations: {
      uploads?: { file: File; isCover?: boolean }[];
      deletions?: ProductMedia[];
      reorder?: ProductMedia[];
    },
  ): Observable<ProductMedia[]>;

  abstract updateMediaItem(
    product: Product,
    mediaId: string,
    updates: Partial<ProductMedia>,
  ): Observable<ProductMedia>;

  abstract deleteMediaItem(product: Product, mediaId: string): Observable<void>;

  abstract configureMeetingPickup(
    product: Product,
    settings: MeetingPickUpSetting,
  ): Observable<Product>;

  abstract configureTourDetails(
    product: Product,
    details: {
      durationMinutes: number;
      flexibleDuration?: { from: number; to: number };
      difficultyLevel?: string;
      highlights?: string;
      itineraryStops?: ItineraryStop[];
    },
  ): Observable<Product>;

  abstract configureActivityDetails(
    product: Product,
    details: {
      durationMinutes: number;
      difficultyLevel?: string;
    },
  ): Observable<Product>;

  abstract manageSchedulePatterns(
    product: Product,
    operations: {
      patterns: SchedulePattern[];
      deletions?: SchedulePattern[];
    },
  ): Observable<SchedulePattern[]>;

  abstract generateSessions(
    product: Product,
    dateRange: { start: Date; end: Date },
  ): Observable<ProductSession[]>;

  abstract configureBookingSettings(
    product: Product,
    settings: {
      confirmationType: "manual" | "instant";
      bookingCutoffType: "START_TIME" | "FIXED";
      defaultBookingCutoffHours?: number;
      bookingCutoffFixedTime?: string;
      bookingCutoffFixedTimeOnDay?: number;
    },
  ): Observable<Product>;

  abstract isAllowUpdatePricingUnit(product: Product): Observable<boolean>;

  abstract updateBookingSettings(
    product: Product,
    settings: {
      priceUnit: PriceUnit;
      priceUnitGroup?: GroupDefinitionEnum;
      ageBandDefinitions?: AgeDefinition;
    },
  ): Observable<Product>;

  abstract setCancellationPolicy(
    product: Product,
    policy: CancellationPolicy,
  ): Observable<Product>;

  abstract manageAccessibility(
    product: Product,
    items: ProductAccessibility[],
  ): Observable<Product>;

  abstract manageHealthSafety(
    product: Product,
    items: ProductHealthSafety[],
  ): Observable<Product>;

  abstract manageFeatures(
    product: Product,
    operations: {
      inclusions?: ProductFeature[];
      exclusions?: ProductFeature[];
    },
  ): Observable<ProductFeature[]>;

  abstract manageAddons(
    product: Product,
    operations: {
      addons: ProductAddon[];
      deletions?: ProductAddon[];
    },
  ): Observable<ProductAddon[]>;

  abstract addNewSeason(
    product: Product,
    season: ProductSeason,
  ): Observable<Product>;

  abstract addSeasonToOption(
    product: Product,
    productOptionId: string,
    season: ProductSeason,
  ): Observable<Product>;

  abstract createSeasonForOption(
    product: Product,
    productOptionId: string,
    seasonData: ProductSeason,
    pricingRecordData: PricingRecord,
  ): Observable<void>;

  abstract addNewProductOption(
    product: Product,
    productOption: ProductOption,
  ): Observable<Product>;

  abstract updateProductOption(
    product: Product,
    productOption: ProductOption,
  ): Observable<void>;

  abstract deleteProductOption(
    productId: string,
    optionId: string,
  ): Observable<void>;

  abstract deleteSeason(productId: string, seasonId: string): Observable<void>;

  abstract updateSeason(
    productId: string,
    seasonId: string,
    seasonData: Partial<ProductSeason>,
  ): Observable<void>;

  abstract updatePricingRecord(
    productId: string,
    optionId: string,
    recordId: string,
    pricingData: Partial<PricingRecord>,
  ): Observable<void>;

  abstract deletePricingRecord(
    productId: string,
    optionId: string,
    recordId: string,
  ): Observable<void>;

  abstract addPricingRecordToSeason(
    productId: string,
    productOptionId: string,
    productSeason: ProductSeason,
    pricingRecord: PricingRecord,
  ): Observable<Product>;

  abstract submitForReview(productId: string): Observable<Product>;
  abstract publish(product: Product): Observable<Product>;
  abstract unpublish(product: Product): Observable<Product>;
  abstract archive(product: Product): Observable<Product>;

  abstract getProduct(
    product: { id: string },
    relations?: string[],
  ): Observable<Product>;
  abstract getProductsByOperator(
    operator: { id: string },
    limit: number,
    page: number,
    filters?: {
      status?: ProductStatus;
      type?: ProductType;
      search?: string;
    },
  ): Observable<Product[]>;

  abstract getAvailableCancellationPolicies(operator: {
    id: string;
  }): Observable<CancellationPolicy[]>;
  abstract getProductCategories(): Observable<ProductCategory[]>;

  getStatusLabel(status: ProductStatus): string {
    switch (status) {
      case ProductStatus.PENDING_REVIEW:
        return "Under Review";
      case ProductStatus.PUBLISHED:
        return "Published";
      case ProductStatus.REJECTED:
        return "Rejected";
      case ProductStatus.ARCHIVED:
        return "Archived";
      case ProductStatus.DRAFT:
        return "Draft";
      case ProductStatus.UNPUBLISHED:
        return "Unpublished";
      default:
        return "Unknown";
    }
  }

  abstract getNextStep(product: Product): string;

  abstract getProductsWithOptions(): Observable<
    {
      id: string;
      name: string;
      header?: string;
      description?: string;
      options: { id: string; title: string }[];
    }[]
  >;

  abstract getCommissionRate(): Observable<{ rate: string }>;

  abstract getTaxonomyCategories(facet: string): Observable<any[]>;
  abstract saveTaxonomies(
    product: Product,
    categoryIds: string[],
  ): Observable<Product>;
  abstract finishThemesCategories(product: Product): Observable<Product>;
  abstract generateAffiliateLoginCode(): Observable<{ code: string }>;
  abstract getAffiliateBookingDetails(bookingId: string): Observable<any>;
  abstract cancelAffiliateBooking(
    bookingId: string,
    cancellationReason: string,
  ): Observable<any>;
  abstract getAffiliatePayouts(page?: number, limit?: number): Observable<any>;
  abstract getAffiliatePayoutDetails(payoutId: string): Observable<any>;
  abstract updateOperatorProfile(
    profile: UpdateOperatorProfileDto,
  ): Promise<void>;
}
